import { MUITextField } from "@abb/arcadia-ui-react"
import { StatusCodes } from "http-status-codes"
import { useCallback, useEffect } from "react"
import { Controller, useForm } from "react-hook-form"
import { toast } from "react-toastify"
import { digitalManagerApi } from "../../../../../../../services/api"
import {
  DiMaDetailsContent,
  DiMaDetailsContentProps,
} from "../../../../../../components/DiMaDetailsContent"
import { useApproverTypeSelectRenderer } from "../../../../../../components/Helpers"
import {
  ApplicationRoleApproverGet,
  ApplicationRoleApproverPut,
} from "../../../../../../models/ApplicationRoleApprover"
import {
  AccessLevel,
  DimaFeatureShortNameEnum,
  hasUserAccessToApplicationFeature,
} from "../../../../../../models/Authorization"
import { UserInfo } from "../../../../../../models/User"

export function ApplicationRoleApproverDetails(props: {
  readonly approverDetails: ApplicationRoleApproverGet
  readonly user: UserInfo
  readonly setApprover: (updatedApprover: ApplicationRoleApproverGet) => void
}) {
  const { approverDetails, user, setApprover } = props
  const isGeneric = !(
    approverDetails.properties.length > 0 && approverDetails.properties[0].name !== null
  )

  const formMethods = useForm<ApplicationRoleApproverPut>({
    mode: "onChange",
  })

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
  } = formMethods

  useEffect(() => {
    reset({
      type: approverDetails.properties[0].approverType,
      isAutomatic: false,
    })
  }, [approverDetails.properties, reset])

  const onSubmit = useCallback(
    async (values: ApplicationRoleApproverPut) => {
      try {
        const res = await digitalManagerApi.put<ApplicationRoleApproverGet>(
          `/applications/${approverDetails.application.id}/roles/${approverDetails.role.id}/approvers/${approverDetails.user.id}`,
          {
            type: values.type,
            isAutomatic: false,
          }
        )
        if (res.status === StatusCodes.OK) {
          toast.success("Approver type updated")
          setApprover(res.data)
        }
      } catch {
        toast.error("Cannot update approver type")
      }
    },
    [
      approverDetails.application.id,
      approverDetails.role.id,
      approverDetails.user.id,
      setApprover,
    ]
  )

  const renderTypeSelect = useApproverTypeSelectRenderer({
    errors,
    valueExtractor: (first: unknown) => {
      if (typeof first === "string") {
        return first
      }

      if (typeof first === "object" && first !== null && "id" in first) {
        const { id } = first as { id?: string }
        return id ?? ""
      }

      return ""
    },
  })

  const detailsContentProps: DiMaDetailsContentProps<ApplicationRoleApproverPut> = {
    formMethods,
    canWrite: hasUserAccessToApplicationFeature(
      user.authorizations.applications,
      approverDetails.application.id,
      DimaFeatureShortNameEnum.AM,
      AccessLevel.Write
    ),
    inlineEdit: isGeneric,
    listItems: [
      {
        id: 0,
        label: "Application",
        displayItem: <div className="title">{approverDetails.application.shortName}</div>,
      },
      {
        id: 1,
        label: "Email",
        displayItem: <div className="title">{approverDetails.user.username}</div>,
      },
      {
        id: 2,
        label: "Is generic",
        displayItem: <div className="title">{isGeneric.toString()}</div>,
      },
    ],
  }

  if (isGeneric) {
    detailsContentProps.listItems.push({
      label: "Approver type",
      id: 3,
      displayItem: (
        <div className="title">{approverDetails.properties[0].approverType}</div>
      ),
      editItem: (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Controller
            name="type"
            control={control}
            rules={{ required: "Approver type field is required" }}
            render={renderTypeSelect}
          />
          {errors.type && (
            <MUITextField
              isDisabled
              value=""
              errors={{
                type: {
                  message:
                    typeof errors.type?.message === "string" ? errors.type.message : "",
                },
              }}
            />
          )}
        </div>
      ),
    })
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DiMaDetailsContent {...detailsContentProps} onSubmit={onSubmit} />
    </form>
  )
}
